import type { AuthChangeEvent, Session } from "@supabase/supabase-js";

const STORAGE_KEY = "chickytutor-password-recovery-v1";
const MAX_AGE_MS = 60 * 60 * 1000;
type Marker = { userId: string; sessionId: string; startedAt: number };

// This is a UI continuity hint, not authorization. Supabase authorizes the write.
// Bind it to the auth session without persisting another copy of its credentials.
function sessionId(session: Session | null): string | null {
  try {
    const payload = session?.access_token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")));
    return typeof claims.session_id === "string" ? claims.session_id : null;
  } catch {
    return null;
  }
}

export function createPasswordRecoveryStore(
  storage: () => Storage | undefined = () =>
    typeof window === "undefined" ? undefined : window.sessionStorage,
  now: () => number = Date.now
) {
  let marker: Marker | null = null;
  let loaded = false;

  function clear() {
    loaded = true;
    marker = null;
    try {
      storage()?.removeItem(STORAGE_KEY);
    } catch {
      // Recovery still works in memory when browser storage is unavailable.
    }
  }

  function matches(session: Session | null) {
    if (!loaded) {
      loaded = true;
      try {
        marker = JSON.parse(storage()?.getItem(STORAGE_KEY) || "null");
      } catch {
        marker = null;
      }
    }
    const age = marker ? now() - marker.startedAt : NaN;
    return Boolean(
      session &&
      marker &&
      Number.isFinite(age) &&
      age >= 0 &&
      age < MAX_AGE_MS &&
      marker.userId === session.user.id &&
      marker.sessionId === sessionId(session)
    );
  }

  function onAuthStateChange(event: AuthChangeEvent, session: Session | null) {
    if (event === "PASSWORD_RECOVERY" && session) {
      const id = sessionId(session);
      clear();
      if (!id) return;
      marker = { userId: session.user.id, sessionId: id, startedAt: now() };
      try {
        storage()?.setItem(STORAGE_KEY, JSON.stringify(marker));
      } catch {
        // The current page can proceed without refresh persistence.
      }
    } else if (event === "SIGNED_OUT" || !matches(session)) {
      clear();
    }
  }

  return { matches, clear, onAuthStateChange };
}
